import {
  loadDanbooruTags,
  prependMatchedCharacterTag,
} from './danbooruTags.js';
import { resolveBodyOptionId } from './bodyOptionCatalog.js';
import { initOAuthManager } from '../utils/oauthManager.js';
import { showLoginRequired } from './imageGeneration.js';
import { showSuccess, showError, showWarning } from '../utils/UnifiedNotificationSystem.js';
import { populateKvRow, rememberCharacterName, removeEmptyKvRows } from './utils/kvRow.js';
import {
  extractFirstUrl,
  requestBrowsePage,
  requestAiGenerate,
  resolveBrowseStoryContent,
  CHARACTER_BROWSE_INSTRUCTIONS,
} from './generationApi.js';
import { createAiProgressController } from './aiProgressStatus.js';
import { lockFormControls, unlockFormControls } from './utils/formHelpers.js';
import { resetCharacterFormForAssistantFill } from './forms/characterForm.js';
import { stripCharacterGenderTags } from './characterGenderTag.js';
import { applyGenderTranslations } from '../translations.js';

let aiAssistantInitialized = false;
let currentClickHandler = null;

export const CHARACTER_AI_SUCCESS_EVENT = 'character-ai-generation-complete';
export const CHARACTER_AI_ERROR_EVENT = 'character-ai-generation-error';

const ensureTrailingComma = (text) => {
  if (!text || typeof text !== 'string') return text;
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  return trimmed.endsWith(',') ? trimmed : `${trimmed},`;
};

const setFieldValue = (id, val) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.value = val ?? '';
  el.dispatchEvent(new Event('input', { bubbles: true }));
};

const refreshGenderAwareCopy = () => {
  applyGenderTranslations('create-character');
};

const applyAssistantGender = (genderSelect, rawGender) => {
  let nextGender = genderSelect?.value || 'female';
  if (typeof rawGender === 'string') {
    const g = rawGender.toLowerCase().trim();
    if (g === 'male' || g === 'female') nextGender = g;
  }
  if (genderSelect) {
    genderSelect.value = nextGender;
    genderSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }
  refreshGenderAwareCopy();
  return nextGender;
};

const applyBodyOptionId = (gender, bodyOptionId) => {
  const resolvedId = resolveBodyOptionId(gender, bodyOptionId);
  const hiddenInput = document.getElementById('ai-character-bust');
  if (!hiddenInput) return;

  hiddenInput.value = String(resolvedId);

  if (typeof window.setBodySelectionState === 'function') {
    window.setBodySelectionState(resolvedId, { gender, silent: true });
  }
};

export async function initializeCharacterAIAssistant(forceReinit = false) {
  if (aiAssistantInitialized && !forceReinit) {
    return;
  }

  const aiRequest = document.getElementById('ai-request');
  const submitBtn = document.getElementById('submit-ai-request');
  const loading = document.getElementById('ai-loading');

  if (forceReinit) {
    aiAssistantInitialized = false;
    if (currentClickHandler && submitBtn) {
      submitBtn.removeEventListener('click', currentClickHandler);
      currentClickHandler = null;
    }
  }

  if (!aiRequest || !submitBtn || !loading) {
    console.error('Character AI Assistant elements not found in DOM');
    return;
  }

  const progress = createAiProgressController(document.getElementById('ai-progress-status'));

  const step1Form = document.getElementById('step1-form');
  const lockCharacterForm = () => {
    lockFormControls('character-form', ['.back-btn', '.visibility-btn']);
    step1Form?.classList.add('is-assistant-locked');
  };
  const unlockCharacterForm = () => {
    unlockFormControls('character-form', ['.back-btn', '.visibility-btn']);
    step1Form?.classList.remove('is-assistant-locked');
  };

  currentClickHandler = async () => {
    const oauthManager = initOAuthManager();
    if (!(await oauthManager.isAuthenticated())) {
      showLoginRequired();
      document.dispatchEvent(new CustomEvent(CHARACTER_AI_ERROR_EVENT, { detail: { message: 'Authentication required' } }));
      return;
    }

    const userPrompt = aiRequest.value.trim();
    const genderSelect = document.getElementById('ai-character-gender');
    const storyUrl = extractFirstUrl(userPrompt);

    if (!storyUrl && (!genderSelect || !genderSelect.value)) {
      showWarning('Please select a gender before generating the character.', { icon: 'user', duration: 4000 });
      document.dispatchEvent(new CustomEvent(CHARACTER_AI_ERROR_EVENT, { detail: { message: 'Gender required' } }));
      return;
    }
    if (!userPrompt) {
      showWarning('Please enter a request.', { icon: 'edit', duration: 4000 });
      document.dispatchEvent(new CustomEvent(CHARACTER_AI_ERROR_EVENT, { detail: { message: 'Prompt required' } }));
      return;
    }

    loading.classList.add('is-hidden');
    window.isAIAssistantRunning = true;
    lockCharacterForm();

    let hadBodyOptionId = false;
    try {
      const token = await window.tokenManager?.getValidAccessToken?.() ?? null;
      if (!token) throw new Error('Authentication required. Please log in.');

      let promptForGenerate = userPrompt;
      if (storyUrl) {
        progress.startBrowsePhase();
        const browseData = await requestBrowsePage({
          url: storyUrl,
          instructions: CHARACTER_BROWSE_INSTRUCTIONS,
          purpose: 'character',
          token,
          paginate: false,
        });
        const storyContent = resolveBrowseStoryContent(browseData, userPrompt);
        promptForGenerate = `Generate character based on this extracted content: ${storyContent}`;
      }

      progress.startGeneratePhase({ variant: 'character' });
      const aiOutput = await requestAiGenerate({ prompt: promptForGenerate, isCharacter: true }, token);
      if (!aiOutput || typeof aiOutput !== 'object' || aiOutput.error) {
        throw new Error(aiOutput?.error || 'Invalid AI response');
      }

      resetCharacterFormForAssistantFill();

      const inferredGender = applyAssistantGender(genderSelect, aiOutput.gender);

      await loadDanbooruTags();

      // Worker owns appearance/outfit split (incl. catalog apply). Trust fields as-is —
      // do not partitionAppearanceAndOutfit (it moves tags and breaks twin fingerprints).
      const appearance = aiOutput.characterPrompt || aiOutput.character_prompt || aiOutput.prompt || '';
      const outfit = aiOutput.outfit_prompt || aiOutput.outfitPrompt || '';
      const characterPromptWithTag = prependMatchedCharacterTag({
        danbooruCharacterTag: aiOutput.danbooruCharacterTag,
        characterPrompt: appearance,
      });

      setFieldValue('ai-character-name', aiOutput.name);

      const heightStr = String(aiOutput.height || aiOutput.height_cm || '');
      const heightMatch = heightStr.match(/(\d+)/);
      const heightValue = heightMatch ? heightMatch[1] : (aiOutput.height || aiOutput.height_cm || '');

      removeEmptyKvRows();

      populateKvRow('Personality', aiOutput.description || aiOutput.characterDescription || aiOutput.bio);
      populateKvRow('Likes', Array.isArray(aiOutput.likes) ? aiOutput.likes.join(', ') : (aiOutput.likes || ''));
      populateKvRow('Dislikes', Array.isArray(aiOutput.dislikes) ? aiOutput.dislikes.join(', ') : (aiOutput.dislikes || ''));
      populateKvRow('Race', aiOutput.race || aiOutput.ethnicity || '');
      populateKvRow('Occupation', aiOutput.occupation || aiOutput.job || aiOutput.role || '');
      populateKvRow('Birthday', aiOutput.birthdate || aiOutput.birthday || '');
      populateKvRow('Age', aiOutput.age || aiOutput.ageYears || aiOutput.age_years || '');
      populateKvRow('Height', heightValue);
      rememberCharacterName(document.getElementById('ai-character-name')?.value || aiOutput.name);

      const bodyOptionId = aiOutput.bodyOptionId ?? aiOutput.body_option_id;
      hadBodyOptionId = bodyOptionId !== null && bodyOptionId !== undefined && bodyOptionId !== '';
      if (hadBodyOptionId) {
        applyBodyOptionId(inferredGender, bodyOptionId);
      } else if (typeof window.clearBodySelectionState === 'function') {
        window.clearBodySelectionState();
      } else {
        const bustEl = document.getElementById('ai-character-bust');
        if (bustEl) bustEl.value = '';
      }

      const visibleAppearance = stripCharacterGenderTags(characterPromptWithTag);
      setFieldValue('ai-character-prompt', visibleAppearance ? ensureTrailingComma(visibleAppearance) : '');
      setFieldValue('ai-outfit-prompt', outfit ? ensureTrailingComma(outfit) : '');

      if (aiOutput.style) {
        const styleSelect = document.getElementById('ai-character-style');
        if (styleSelect && (aiOutput.style === 'anime' || aiOutput.style === 'realism')) {
          styleSelect.value = aiOutput.style;
          styleSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      const loraActivation = aiOutput.lora_activation || aiOutput.lora;
      if (loraActivation && typeof window.applyCharacterLora === 'function') {
        window.applyCharacterLora(loraActivation);
      }

      showSuccess('Character form auto-filled! Review prompts and generate a preview when ready.', { icon: 'check-circle', duration: 4000 });
      document.dispatchEvent(new CustomEvent(CHARACTER_AI_SUCCESS_EVENT));
    } catch (e) {
      if (e.message !== 'QUOTA_HANDLED') {
        console.error(e);
        showError(e.message, { icon: 'exclamation-circle', duration: 5000 });
      }
      document.dispatchEvent(new CustomEvent(CHARACTER_AI_ERROR_EVENT, { detail: { message: e.message } }));
    } finally {
      progress.clear();
      loading.classList.add('is-hidden');
      window.isAIAssistantRunning = false;

      if (hadBodyOptionId && typeof window.updateAllPromptTags === 'function') {
        const finalGenderSelect = document.getElementById('ai-character-gender');
        const finalGender = finalGenderSelect?.value || 'female';
        window.updateAllPromptTags(finalGender);
      }

      unlockCharacterForm();
      submitBtn.disabled = false;
      refreshGenderAwareCopy();

      const formEl = document.getElementById('character-form');
      if (formEl) {
        formEl.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  };

  submitBtn.addEventListener('click', currentClickHandler);
  aiAssistantInitialized = true;
}
